import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import { useQuery } from "@tanstack/react-query";
import React, { useMemo, useState } from "react";
import FieldCanvas from "./FieldCanvas";
import {
  ApiGameEvents,
  findCoords,
  GameEventType,
  getGameEventType,
  isGoal,
  isTurnover,
} from "./GameEvent";
import { GamePoint } from "./GamePoint";

type PlayerGameStat = any;

type eventInfo = {
  source?: "event" | "pstats";
  player?: any;
  eventType?: GameEventType;
};

interface GameLevelGranularityProps {
  gameID: string;
}

export default function GameLevelGranularity(props: GameLevelGranularityProps) {
  const [hovered, setHovered] = useState<null | {
    point: any;
    left: number;
    top: number;
  }>(null);

  // UI state
  const [metric, setMetric] = useState<"turnovers" | "goals">("turnovers");
  const [positionChoice, setPositionChoice] = useState<
    "start" | "end" | "both"
  >("both");
  const [mode, setMode] = useState<"point" | "heat">("point");
  const [showArrows, setShowArrows] = useState<boolean>(true);

  const { data: eventsResponse, isLoading: loadingEvents } = useQuery<any>({
    queryKey: ["gameEvents", props.gameID],
    enabled: !!props.gameID,
    queryFn: async (): Promise<ApiGameEvents[]> => {
      const url = `https://www.backend.ufastats.com/api/v1/gameEvents?gameID=${encodeURIComponent(
        props.gameID || "",
      )}`;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`gameEvents fetch failed: ${res.status}`);
      const json = await res.json();
      return json.data || json || [];
    },
  });

  const { data: pstatsResponse, isLoading: loadingPStats } = useQuery<any>({
    queryKey: ["playerGameStats", props.gameID],
    enabled: !!props.gameID,
    queryFn: async (): Promise<PlayerGameStat[]> => {
      const url = `https://www.backend.ufastats.com/api/v1/playerGameStats?gameID=${encodeURIComponent(
        props.gameID || "",
      )}`;
      const res = await fetch(url);
      if (!res.ok)
        throw new Error(`playerGameStats fetch failed: ${res.status}`);
      const json = await res.json();
      return json.data || json || [];
    },
  });

  const { points, connections } = useMemo(() => {
    const extracted: Array<{
      x: number;
      y: number;
      role: "start" | "end";
      kind: "goal" | "turnover";
      info?: any;
    }> = [];
    const conns: Array<{
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      kind: "goal" | "turnover";
      info?: any;
    }> = [];

    const pushPoint = (
      x: number,
      y: number,
      role: "start" | "end",
      kind: "goal" | "turnover",
      info?: eventInfo,
    ) => {
      if (x == null || y == null) return;
      extracted.push({ x, y, role, kind, info });
    };

    const allEvents: any[] = [];
    if (eventsResponse) {
      if (Array.isArray(eventsResponse))
        allEvents.push(...(eventsResponse as any[]));
      else {
        if (Array.isArray(eventsResponse.homeEvents))
          allEvents.push(...eventsResponse.homeEvents);
        if (Array.isArray(eventsResponse.awayEvents))
          allEvents.push(...eventsResponse.awayEvents);
        if (eventsResponse.data && Array.isArray(eventsResponse.data))
          allEvents.push(...eventsResponse.data);
      }
    }

    allEvents.forEach((ev: any) => {
      let eventType: GameEventType | undefined = undefined;
      try {
        eventType = getGameEventType(ev.type);
      } catch (e) {
        eventType = undefined;
      }
      if (
        ev &&
        ((metric === "goals" && !isGoal(ev)) ||
          (metric === "turnovers" && !isTurnover(ev)))
      )
        return;
      if (!eventType && (metric === "goals" || metric === "turnovers")) return;

      const { startCoord, endCoord } = findCoords(
        GamePoint.createPoint(0, "none", ev.teamId),
        ev,
      );

      if (startCoord && endCoord && eventType) {
        conns.push({
          x1: startCoord.x,
          y1: startCoord.y,
          x2: endCoord.x,
          y2: endCoord.y,
          kind: isGoal(ev) ? "goal" : isTurnover(ev) ? "turnover" : "goal",
          info: { source: "event", event: ev },
        });
      }
      if (
        (positionChoice === "start" || positionChoice === "both") &&
        startCoord
      )
        pushPoint(
          startCoord.x,
          startCoord.y,
          "start",
          isGoal(ev) ? "goal" : isTurnover(ev) ? "turnover" : "goal",
          { source: "event", eventType },
        );
      if ((positionChoice === "end" || positionChoice === "both") && endCoord)
        pushPoint(
          endCoord.x,
          endCoord.y,
          "end",
          isGoal(ev) ? "goal" : isTurnover(ev) ? "turnover" : "goal",
          { source: "event", eventType },
        );
    });

    if (
      extracted.length === 0 &&
      pstatsResponse &&
      Array.isArray(pstatsResponse)
    ) {
      if (metric === "turnovers") {
        const pts: typeof extracted = [];
        pstatsResponse.forEach((p: any) => {
          const count = p.throwaways || 0;
          for (let i = 0; i < count; i++) {
            const seed = hashString(
              (p.player && p.player.playerID) || JSON.stringify(p) + i,
            );
            const x = (seed % 1000) / 1000;
            const y = ((seed >> 10) % 1000) / 1000;
            if (positionChoice === "start" || positionChoice === "both")
              pts.push({
                x,
                y,
                role: "start",
                kind: "turnover",
                info: { source: "pstats", player: p },
              });
            if (positionChoice === "end" || positionChoice === "both")
              pts.push({
                x: x,
                y: y,
                role: "end",
                kind: "turnover",
                info: { source: "pstats", player: p },
              });
          }
        });
        return { points: pts, connections: [] };
      }
      if (metric === "goals") {
        const pts: typeof extracted = [];
        pstatsResponse.forEach((p: any) => {
          const count = p.goals || 0;
          for (let i = 0; i < count; i++) {
            const seed = hashString(
              (p.player && p.player.playerID) || JSON.stringify(p) + i + "g",
            );
            const x = (seed % 1000) / 1000;
            const y = ((seed >> 10) % 1000) / 1000;
            if (positionChoice === "start" || positionChoice === "both")
              pts.push({
                x,
                y,
                role: "start",
                kind: "goal",
                info: { source: "pstats", player: p },
              });
            if (positionChoice === "end" || positionChoice === "both")
              pts.push({
                x: x,
                y: y,
                role: "end",
                kind: "goal",
                info: { source: "pstats", player: p },
              });
          }
        });
        return { points: pts, connections: [] };
      }
    }

    return { points: extracted, connections: conns };
  }, [eventsResponse, pstatsResponse, metric, positionChoice]);

  // Canvas rendering moved to FieldCanvas component

  if (!props.gameID) return <Typography>Missing game ID</Typography>;

  const loading = loadingEvents || loadingPStats;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", mt: 1 }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="metric-label">Metric</InputLabel>
          <Select
            labelId="metric-label"
            value={metric}
            label="Metric"
            onChange={(e: SelectChangeEvent) =>
              setMetric(e.target.value as any)
            }
          >
            <MenuItem value="turnovers">Turnovers</MenuItem>
            <MenuItem value="goals">Goals</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="position-label">Position</InputLabel>
          <Select
            labelId="position-label"
            value={positionChoice}
            label="Position"
            onChange={(e: SelectChangeEvent) =>
              setPositionChoice(e.target.value as any)
            }
          >
            <MenuItem value="start">Start</MenuItem>
            <MenuItem value="end">End</MenuItem>
            <MenuItem value="both">Both</MenuItem>
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id="mode-label">Mode</InputLabel>
          <Select
            labelId="mode-label"
            value={mode}
            label="Mode"
            onChange={(e: SelectChangeEvent) => setMode(e.target.value as any)}
          >
            <MenuItem value="point">Point</MenuItem>
            <MenuItem value="heat">Heat</MenuItem>
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={showArrows}
              onChange={(e) => setShowArrows(e.target.checked)}
              size="small"
            />
          }
          label="Arrows"
        />
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 2 }}>
          <CircularProgress size={20} />{" "}
          <Typography>Loading game data...</Typography>
        </Box>
      ) : (
        <Paper sx={{ mt: 2, p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Showing {metric} — {positionChoice} positions — {mode} mode
          </Typography>
          <div style={{ width: "100%", height: 600, position: "relative" }}>
            <FieldCanvas
              points={points}
              connections={connections}
              mode={mode}
              positionChoice={positionChoice}
              showArrows={showArrows}
              onHover={(h) => setHovered(h)}
              style={{ maxWidth: 400, height: "100%" }}
            />
            {hovered && hovered.point && (
              <Paper
                elevation={3}
                style={{
                  position: "absolute",
                  pointerEvents: "none",
                  left: `${hovered.left}px`,
                  top: `${hovered.top}px`,
                  zIndex: 10,
                }}
                sx={{ p: 1 }}
              >
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {hovered.point.kind.toUpperCase()} ({hovered.point.role})
                </Typography>
                <Typography variant="caption" display="block">
                  {hovered.point.info?.source === "event" &&
                  hovered.point.info?.event?.typeName
                    ? String(hovered.point.info.event.typeName)
                    : hovered.point.info?.player?.playerID ||
                      hovered.point.info?.player?.player?.playerID ||
                      ""}
                </Typography>
                {hovered.point.info && (
                  <Typography variant="caption" display="block">
                    {hovered.point.info.source === "event" &&
                      hovered.point.info.event && (
                        <span>
                          {hovered.point.info.event.description ||
                            hovered.point.info.event.typeName ||
                            JSON.stringify(hovered.point.info.event)}
                        </span>
                      )}
                  </Typography>
                )}
              </Paper>
            )}
          </div>
        </Paper>
      )}
    </Box>
  );
}

function hashString(s: string) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return h >>> 0;
}
